/**
 * @module utils/collision
 * Geometric primitives for slash–fruit collision detection.
 *
 * The core test is "does a line segment (the slash) intersect a circle
 * (the fruit hitbox)?". This runs every tracking frame for every live fruit
 * so it is kept allocation-free.
 */

import type { Vec2 } from "./vector.js";
import { sub, dot, lengthSq } from "./vector.js";

/**
 * Tests whether the line segment from `p1` to `p2` intersects a circle
 * centred at `centre` with the given `radius`.
 *
 * Uses the closest-point-on-segment formula; no heap allocations.
 *
 * @returns True if the segment passes through or touches the circle.
 */
export function segmentIntersectsCircle(
  p1: Vec2,
  p2: Vec2,
  centre: Vec2,
  radius: number,
): boolean {
  const d = sub(p2, p1);
  const f = sub(p1, centre);

  const dLenSq = lengthSq(d);

  if (dLenSq === 0) {
    // Degenerate segment — test point vs circle.
    return lengthSq(f) <= radius * radius;
  }

  let t = -dot(f, d) / dLenSq;
  t = Math.max(0, Math.min(1, t));

  const closest: Vec2 = {
    x: p1.x + t * d.x,
    y: p1.y + t * d.y,
  };

  return lengthSq(sub(closest, centre)) <= radius * radius;
}

/**
 * Shortest Euclidean distance from point `p` to the line segment (a → b).
 * Pure function, no side effects.
 */
export function distPointToSegment(p: Vec2, a: Vec2, b: Vec2): number {
  const ab = sub(b, a);
  const abLenSq = lengthSq(ab);

  if (abLenSq === 0) {
    // Degenerate segment — distance from p to point a.
    return Math.hypot(p.x - a.x, p.y - a.y);
  }

  // Project p onto the line a→b, clamped to [0,1].
  let t = dot(sub(p, a), ab) / abLenSq;
  t = Math.max(0, Math.min(1, t));

  const closest: Vec2 = {
    x: a.x + t * ab.x,
    y: a.y + t * ab.y,
  };

  return Math.hypot(p.x - closest.x, p.y - closest.y);
}

/**
 * Returns true if the line segment (a → b) intersects a circle at center `c`
 * with radius `r`.
 */
export function lineVsCircle(a: Vec2, b: Vec2, c: Vec2, r: number): boolean {
  return distPointToSegment(c, a, b) <= r;
}
